import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowUpRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BLOG_POSTS } from "@/lib/blog";

/**
 * Minimal safe markdown renderer for our blog body content.
 * Supports: ### headings, **bold**, *italics*, [text](url) links, lists (- or 1.), paragraphs.
 * No raw HTML injection — everything is escaped first.
 */
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let listType = null;
  let listItems = [];

  const flushList = () => {
    if (listType && listItems.length) {
      const Tag = listType === "ol" ? "ol" : "ul";
      out.push(`<${Tag} class="my-4 space-y-2 pl-6 ${Tag === "ol" ? "list-decimal" : "list-disc"} text-[#143D63]/85">${listItems.join("")}</${Tag}>`);
    }
    listType = null;
    listItems = [];
  };

  const inline = (raw) => {
    let s = escapeHtml(raw);
    // links [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, t, u) => `<a href="${u}" class="text-[#8B0000] font-semibold underline underline-offset-2 hover:text-[#143D63]">${t}</a>`);
    // bold **x**
    s = s.replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>");
    // italics *x*
    s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<i>$2</i>");
    // inline code `x`
    s = s.replace(/`([^`]+)`/g, '<code class="bg-[#143D63]/8 px-1.5 py-0.5 text-[13px]">$1</code>');
    return s;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushList(); out.push(""); continue; }
    if (line.startsWith("### ")) {
      flushList();
      out.push(`<h3 class="font-serif text-2xl sm:text-3xl text-[#143D63] mt-10 mb-4 leading-tight">${inline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith("## ")) {
      flushList();
      out.push(`<h2 class="font-serif text-3xl text-[#143D63] mt-12 mb-4">${inline(line.slice(3))}</h2>`);
      continue;
    }
    if (/^\s*-\s+/.test(line)) {
      if (listType !== "ul") { flushList(); listType = "ul"; }
      listItems.push(`<li>${inline(line.replace(/^\s*-\s+/, ""))}</li>`);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      if (listType !== "ol") { flushList(); listType = "ol"; }
      listItems.push(`<li>${inline(line.replace(/^\s*\d+\.\s+/, ""))}</li>`);
      continue;
    }
    flushList();
    out.push(`<p class="my-4 text-[15px] leading-[1.75] text-[#143D63]/85">${inline(line)}</p>`);
  }
  flushList();
  return out.join("");
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const related = BLOG_POSTS.filter((_, i) => i !== idx).slice(0, 2);

  const share = async () => {
    const url = `https://thezebo.com/blog/${post.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch (_) { /* user cancelled */ }
  };

  return (
    <main data-testid="blog-post" className="pt-32 pb-24 bg-[#F9F8F6] relative z-10">
      <Seo title={`${post.title} — ZEBO Journal`} description={post.excerpt} path={`/blog/${post.slug}`} image={post.hero} />

      <article className="max-w-[820px] mx-auto px-5 sm:px-8">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-xs text-[#143D63]/60 hover:text-[#8B0000] mb-8">
          <ArrowLeft size={14} /> All journal articles
        </Link>

        <p className="overline text-[#8B0000] mb-4">{post.category}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#143D63] leading-[1] tracking-tight">{post.title}</h1>
        <p className="text-[#143D63]/70 mt-6 text-lg leading-relaxed">{post.excerpt}</p>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pb-8 border-b border-[#143D63]/15">
          <div className="flex items-center gap-6 text-xs text-[#143D63]/60">
            <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {post.readMins} min read</span>
            <span>By {post.author}</span>
          </div>
          <button
            onClick={share}
            data-testid="blog-share-btn"
            className="inline-flex items-center gap-2 border border-[#143D63]/25 text-[#143D63] px-4 py-2 text-xs font-bold hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors"
          >
            <Share2 size={13} /> Share
          </button>
        </div>

        <img src={post.hero} alt={post.title} className="mt-10 w-full aspect-[16/9] object-cover" loading="eager" />

        <div
          data-testid="blog-body"
          className="mt-10 max-w-none"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
        />

        {/* CTA */}
        <div className="mt-14 border-y border-[#143D63]/15 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-2xl text-[#143D63] leading-tight">Ready to price your own project?</p>
            <p className="text-[#143D63]/70 text-sm mt-1">Free site visit. Free 3D render. Real BOQ inside 24 hours.</p>
          </div>
          <Link to="/consultation" data-testid="blog-cta-book" className="inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-6 py-3.5 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors">
            Book a free visit <ArrowUpRight size={15} />
          </Link>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-14">
            <p className="overline text-[#8B0000] mb-4">Keep reading</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group block border border-[#143D63]/15 bg-white hover:border-[#F8C21C] transition-colors">
                  <div className="aspect-[16/9] overflow-hidden bg-[#143D63]/5">
                    <img src={r.hero} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <p className="overline text-[#8B0000] text-[10px]">{r.category}</p>
                    <h4 className="font-serif text-lg text-[#143D63] mt-2 leading-tight group-hover:text-[#8B0000] transition-colors">{r.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </main>
  );
}
